import { posthog } from 'posthog-js';
import type { CaptureResult } from 'posthog-js';

import {
  isIgnoredExceptionMessage,
  normalizeExceptionMessage,
  normalizePath,
  redactUrlParams,
} from './posthog-sanitize';

/**
 * Cleans up the `$exception` stream on the way out (HHOS-1966):
 *  - drops non-actionable browser noise outright, and
 *  - strips ids from the message so one defect is one row.
 *
 * Both `$exception_values` (the flat array PostHog groups and queries on — note
 * the singular `$exception_value` is never populated) and `$exception_list`
 * (the structured payload the issue detail view renders) carry the text, so
 * both are rewritten or the two views disagree.
 */
function sanitizeExceptionEvent(event: CaptureResult): CaptureResult | null {
  const properties = event.properties;
  if (!properties) return event;

  const values = properties.$exception_values;
  if (Array.isArray(values)) {
    if (values.some((v) => typeof v === 'string' && isIgnoredExceptionMessage(v))) return null;
    properties.$exception_values = values.map((v) =>
      typeof v === 'string' ? normalizeExceptionMessage(v) : v
    );
  }

  const list = properties.$exception_list;
  if (Array.isArray(list)) {
    for (const item of list) {
      if (item && typeof item === 'object' && typeof item.value === 'string') {
        if (isIgnoredExceptionMessage(item.value)) return null;
        item.value = normalizeExceptionMessage(item.value);
      }
    }
  }

  return event;
}

const PROD_HOSTNAME = 'hhos.honeyhomes.com';

/**
 * Classifies the current host. Production is an explicit allowlist of the one
 * canonical prod hostname — every other host (localhost, `*.run.app` previews,
 * raw Cloud Run URLs) is non-production and, via the prod-only gate in
 * initPostHog, will NOT capture. This fails closed on purpose: an unrecognized
 * host means missing analytics, never dev/preview traffic mis-attributed as
 * prod. If prod ever serves from another hostname, add it here.
 */
function detectEnvironment(hostname: string): 'development' | 'staging' | 'production' {
  if (hostname === PROD_HOSTNAME) return 'production';
  if (hostname === 'localhost' || hostname === '127.0.0.1') return 'development';
  return 'staging';
}

type PostHogBootConfig = { key?: string; email?: string | null; personId?: string | null };

/**
 * Boots PostHog product analytics for hhOS.
 *
 * Called from an effect in root.tsx, and it must stay there. PostHog loads its
 * recorder / exception-capture bundles by inserting <script> elements as direct
 * children of <body>, and entry.client.tsx hydrates the entire document — so
 * booting before hydration commits leaves <body> holding children React never
 * rendered, aborting hydration with React error #418. That multiplied hhOS
 * hydration errors ~12x in production (HHOS-1892). An effect is the earliest
 * point guaranteed to run after the hydration commit, and it still runs when
 * hydration falls back to client rendering, so capture survives a mismatch.
 *
 * Reads the capture key + staff email from a `window.__hhPosthog` global that
 * root.tsx injects from loader data (Doppler in dev, GCP Cloud Run env in
 * prod), so the key is never hardcoded.
 *
 * Session recording is ON but sampled — the sample rate lives in the PostHog
 * project settings (start at 5%) so it can be tuned without a deploy. hhOS is
 * an internal staff tool, so masking is intentionally light: we mask typed
 * input values (`maskAllInputs`) but leave the rendered UI visible so replays
 * and click analytics stay useful.
 *
 * Captures from PRODUCTION ONLY — dev and staging never send events, so the
 * hhOS project holds real staff usage exclusively.
 */
export function initPostHog() {
  const config = (window as unknown as { __hhPosthog?: PostHogBootConfig }).__hhPosthog;
  if (!config?.key) return;

  const environment = detectEnvironment(window.location.hostname);
  if (environment !== 'production') return;

  posthog.init(config.key, {
    api_host: 'https://us.i.posthog.com',
    defaults: '2025-05-24',
    // Capture `$dead_click` events (clicks that resolve to nothing) alongside
    // the `$rageclick` events autocapture already emits. Dead-click capture is
    // a separate, off-by-default instrument, so it must be opted into
    // explicitly. Lets us see unresponsive affordances (disabled/loading
    // buttons, non-interactive elements) in the same tool as rageclicks.
    capture_dead_clicks: true,
    // Capture Core Web Vitals (LCP/INP/CLS/FCP) as `$web_vitals` events, off by
    // default. Lets us slice real-user performance by route/feature alongside
    // product behavior in PostHog (Datadog RUM also tracks these; this keeps a
    // product-analytics-joinable copy next to the usage events).
    capture_performance: { web_vitals: true },
    // Recording enabled; sampled server-side via the project's replay sample
    // rate. Light masking for an internal tool: hide typed input values, keep
    // everything else visible.
    disable_session_recording: false,
    session_recording: {
      maskAllInputs: true,
      // Protect customer PII in replays. `ph-no-capture` is PostHog's default
      // recording blockClass, so tagged elements are fully excluded from the
      // recording — text AND attributes (e.g. tel:/mailto: hrefs) — and are
      // likewise excluded from autocapture. Also set as maskTextSelector here
      // as explicit reinforcement. Rendered PII surfaces carry this class.
      maskTextSelector: '.ph-no-capture',
    },
    person_profiles: 'identified_only',
    // Capture uncaught errors and unhandled promise rejections as `$exception`
    // events so we can see where hhOS breaks for staff. Console errors are left
    // off — they're noisy (framework/third-party warnings) and mostly duplicate
    // the thrown errors we already get here.
    capture_exceptions: {
      capture_unhandled_errors: true,
      capture_unhandled_rejections: true,
      capture_console_errors: false,
    },
    // `mask_personal_data_properties` redacts known PII (emails/names in URLs,
    // etc.) from event properties. Rendered UI text is deliberately left
    // visible — this is a staff-only internal tool. To exclude a specific
    // sensitive element from capture, add the `ph-no-capture` class to it.
    mask_personal_data_properties: true,
    // Exceptions are filtered and normalized here rather than in
    // `sanitize_properties` because dropping an event outright requires
    // returning null, which only `before_send` can do.
    before_send: (event) => {
      if (!event) return event;
      if (event.event === '$exception') return sanitizeExceptionEvent(event);
      return event;
    },
    sanitize_properties: (props) => {
      if (typeof props.$pathname === 'string') {
        props.$pathname = normalizePath(props.$pathname);
      }
      // PostHog masks `$pathname` but leaves the previous-pageview copy raw, so
      // home UUIDs were still reaching the stream through it (HHOS-1966).
      if (typeof props.$prev_pageview_pathname === 'string') {
        props.$prev_pageview_pathname = normalizePath(props.$prev_pageview_pathname);
      }
      if (typeof props.$current_url === 'string') {
        try {
          const url = new URL(props.$current_url);
          url.pathname = normalizePath(url.pathname);
          props.$current_url = redactUrlParams(url.toString());
        } catch {
          // leave $current_url untouched if it isn't a parseable URL
        }
      }
      // A shared region-map link carries the searched address in the query
      // string, so it rides along in the referrer of everything the recipient
      // does next.
      if (typeof props.$referrer === 'string') {
        props.$referrer = redactUrlParams(props.$referrer);
      }
      return props;
    },
  });

  // `person_id` (the staff Person UUID) rides on every event as a super property
  // so agent product-analytics can be joined to the Postgres agent tables
  // (Ticket.currentAssigneeId, Message.senderId, …) by id, not email.
  posthog.register({
    app: 'hhos',
    environment,
    ...(config.personId ? { person_id: config.personId } : {}),
  });

  // Distinct id stays the email to preserve person continuity with historical
  // events; person_id is attached as a queryable person property for grouping.
  if (config.email) {
    posthog.identify(config.email, config.personId ? { person_id: config.personId } : undefined);
  }
}
